/**
 * Progress - Progress Bar Component
 *
 * Visual indicator for completion percentage.
 * Renders as Box with inner filled bar.
 *
 * @example
 * <Progress value={75} />
 * <Progress value={50} variant="success" />
 */
import { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Box } from "@/shared/ui/base";
import { cn } from "@/shared/lib/utils/cn";
import type { BaseComponentProps } from "@/shared/ui/base";

const progressVariants = cva("h-full transition-all", {
  variants: {
    variant: {
      default: "bg-red-600",
      success: "bg-green-600",
      warning: "bg-yellow-600",
      error: "bg-red-600",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

export interface ProgressProps
  extends BaseComponentProps, VariantProps<typeof progressVariants> {
  /** Progress value (0-100) */
  value: number;
  /** Show percentage text */
  showLabel?: boolean;
}

/**
 * Progress component
 * Renders as a Box with filled progress bar
 */
export const Progress = forwardRef<HTMLDivElement, ProgressProps>(
  ({ value, variant = "default", showLabel, className, ...props }, ref) => {
    const clampedValue = Math.min(Math.max(value, 0), 100);

    return (
      <Box ref={ref} className={cn("space-y-1", className)} {...props}>
        <Box className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <Box
            className={progressVariants({ variant })}
            style={{ width: `${clampedValue}%` }}
          />
        </Box>
        {showLabel && (
          <Box className="text-xs text-gray-600 text-right">
            {clampedValue}%
          </Box>
        )}
      </Box>
    );
  },
);

Progress.displayName = "Progress";
